export const tenantId = () => localStorage.getItem('tenantId');

export const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  Accept: 'application/json',
  ...(localStorage.getItem('token') && { Authorization: `Bearer ${localStorage.getItem('token')}` }),
});

export const webhookApiBase = () => `${BACKEND_API}webhooks`;

export const publicIngressUrl = () => {
  const base = (
    process.env.REACT_APP_WEBHOOK_PUBLIC_URL
    || process.env.REACT_APP_WEBHOOK_SERVICE_URL
    || process.env.REACT_APP_SPRING_BACKEND_URL
    || process.env.REACT_APP_BACKEND_URL
    || 'http://localhost:3013/'
  ).replace(/\/?$/, '/');
  return `${base}webhooks/inbound`;
};

export const BACKEND_API = `${(process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002/').replace(/\/?$/, '/')}`;
export const MASTER_CONNECTOR_API = `${BACKEND_API}synctrack/master-connector`;
const TENANT_AGENTS_API = `${BACKEND_API}asrosync/agents/tenant`;

const ACCOUNT_PARAM_KEYS = new Set([
  'realmid', 'realm_id', 'company_id', 'companyid', 'account_id', 'accountid',
  'email', 'user_email', 'team_id', 'teamid', 'workspace_id', 'workspaceid',
]);

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const normalizeName = (value) => value.trim().toLowerCase().replace(/\s+/g, '_');

export const formatApplicationLabel = (value) => {
  if (!value) return '';
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
};

export const connectorLabel = (c) => {
  const desc = c.description || c.ctdDescription;
  const status = c.status || c.ctdStatus;
  const id = c.id || c.ctdId;
  return desc ? `${desc} (${status || 'unknown'})` : `${id}`;
};

export const resolveAccountIdentifier = async (ctdId) => {
  const headers = getAuthHeaders();
  const connRes = await fetch(`${BACKEND_API}synctrack/sync-connectors/${ctdId}`, { headers });
  if (connRes.ok) {
    const conn = await connRes.json();
    if (conn.slackTeamId) return conn.slackTeamId;
  }

  const metaRes = await fetch(`${BACKEND_API}synctrack/sync-connectors/${ctdId}/routing-metadata`, { headers });
  if (metaRes.ok) {
    const meta = await metaRes.json();
    const rm = meta.routingMetadata || {};
    if (rm.email_address) return String(rm.email_address).trim();
  }

  const paramRes = await fetch(`${BACKEND_API}synctrack/sync-connector-parameters/connector/${ctdId}`, { headers });
  if (paramRes.ok) {
    const params = await paramRes.json();
    if (Array.isArray(params)) {
      for (const p of params) {
        const name = (p.scpName || '').trim().toLowerCase();
        const value = (p.scpValue || '').trim();
        if (value && ACCOUNT_PARAM_KEYS.has(name)) return value;
      }
    }
  }

  return '';
};

export const statusColor = (status) => {
  if (status === 'forwarded') return 'success';
  if (status === 'failed') return 'error';
  return 'default';
};

const parseList = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.content)) return data.content;
  return [];
};

export const normalizeStaticTask = (task) => ({
  staticTaskId: task.staticTaskId || task.static_task_id || task.id,
  agentName: task.agentName || task.agent_name || '',
  taskName: task.taskName || task.task_name || '',
  runType: task.runType || task.run_type || '',
  isActive: task.isActive ?? task.is_active ?? true,
});

export const isEventBasedTask = (task) =>
  String(task.runType || '').trim().toUpperCase() === 'EVENT_BASED';

/** Map tenant agent instance UUID → display name (same as AI Agents drawer). */
export const buildAgentLabelMap = (agents) => {
  const map = {};
  (agents || []).forEach((a) => {
    const label = a.agentName || a.agent_name;
    if (!label) return;
    const keys = [
      a.tenantAgentInstanceId,
      a.tenant_agent_instance_id,
      a.agentUniqueId,
      a.agent_unique_id,
      a.tenantAgentId,
      a.tenant_agent_id,
      a.agentName,
      a.agent_name,
    ].filter(Boolean);
    keys.forEach((k) => {
      map[String(k)] = label;
    });
  });
  return map;
};

export const resolveAgentLabel = (agentKey, agentLabelMap) => {
  if (!agentKey) return 'Unknown agent';
  const key = String(agentKey);
  if (agentLabelMap[key]) return agentLabelMap[key];
  if (UUID_RE.test(key)) return 'Agent';
  return key;
};

export const fetchTenantAgents = async (tid) => {
  if (!tid) return [];
  const res = await fetch(`${TENANT_AGENTS_API}/${encodeURIComponent(tid)}`, { headers: getAuthHeaders() });
  if (!res.ok) return [];
  return parseList(await res.json());
};

/** Load tenant destinations for webhook route mapping (no manual UUID entry). */
export const fetchWebhookRouteDestinations = async (tid) => {
  if (!tid) {
    return { tenantAgents: [], staticTasks: [], syncFlows: [], hubFlows: [] };
  }
  const headers = getAuthHeaders();
  const base = BACKEND_API;
  const [agentsRes, tasksRes, syncRes, hubRes] = await Promise.all([
    fetch(`${TENANT_AGENTS_API}/${encodeURIComponent(tid)}`, { headers }),
    fetch(`${base}synctrack/agent/static-tasks/tenant/${encodeURIComponent(tid)}`, { headers }),
    fetch(`${base}synctrack/sync-flow-instances/tenant/${encodeURIComponent(tid)}/group/SyncFlow?page=0&size=1000`, { headers }),
    fetch(`${base}synctrack/sync-flow-instances/tenant/${encodeURIComponent(tid)}/group/HubFlow`, { headers }),
  ]);

  const tenantAgents = agentsRes.ok ? parseList(await agentsRes.json()) : [];
  let staticTasks = [];
  if (tasksRes.ok) {
    staticTasks = parseList(await tasksRes.json())
      .map(normalizeStaticTask)
      .filter((t) => isEventBasedTask(t) && t.isActive !== false);
  } else {
    console.error('Failed to load static tasks for webhook routes', tasksRes.status);
  }

  const syncFlows = syncRes.ok ? parseList(await syncRes.json()) : [];
  const hubFlows = hubRes.ok ? parseList(await hubRes.json()) : [];

  return { tenantAgents, staticTasks, syncFlows, hubFlows };
};

export const registryEventLabel = (row) => {
  const app = formatApplicationLabel(row.application_name || row.applicationName || '');
  const event = row.event_name || row.eventName || '';
  const desc = row.event_description || row.eventDescription || '';
  if (!app && !event) return '';
  const core = `${app} → ${event}`;
  return desc ? `${core} — ${desc}` : core;
};

export const syncFlowLabel = (flow) => {
  const desc = flow.sfiDescription || flow.sfi_description;
  if (desc && String(desc).trim()) return String(desc).trim();
  return 'Unnamed Syncflow';
};

export const hubFlowLabel = (flow) => {
  const desc = flow.sfiDescription || flow.sfi_description;
  if (desc && String(desc).trim()) return String(desc).trim();
  return 'Unnamed Hubflow';
};

export const staticTaskPickerLabel = (task, agentLabelMap) => {
  const taskName = task.taskName || task.task_name || 'Task';
  const agent = resolveAgentLabel(task.agentName || task.agent_name, agentLabelMap);
  return `${agent} — ${taskName}`;
};
